import React, { useEffect } from 'react';
import { View } from '@adobe/react-spectrum';
import { FormFieldComponent } from '../../CreateMoment/FormField';
import { FormField } from '../../store/slice';
import { updateFieldVisibility } from '../utils/fieldVisibility';
import styles from './CustomComponentField.module.scss';

interface CustomComponentFieldProps {
  field: FormField;
  value: any;
  onChange: (fieldName: string, value: any) => void;
  formValues: Record<string, any>;
  hasSubmitted?: boolean;
}

/**
 * Normalizes date values for comparison
 * Handles different date formats: date strings, month strings, quarter strings, special values
 */
const getComparableDate = (dateValue: any): string => {
  if (!dateValue) return '';

  // Handle direct string values (like "Oct 2026", "Q4 2026", "2026-10-15")
  if (typeof dateValue === 'string') {
    return dateValue.trim();
  }

  // Handle DateComponent format
  if (typeof dateValue === 'object') {
    if (dateValue.type === 'months' && dateValue.months?.length > 0) {
      return dateValue.months[0].trim();
    }
    if (dateValue.type === 'quarters' && dateValue.quarters?.length > 0) {
      return dateValue.quarters[0].trim();
    }
    if (dateValue.type === 'date' && dateValue.date) {
      return dateValue.date.trim();
    }
    if (dateValue.type === 'special') {
      return dateValue.value || 'N/A';
    }
  }

  return '';
};

/**
 * Checks if two dates are the same
 */
const areDatesEqual = (date1: any, date2: any): boolean => {
  const comparable1 = getComparableDate(date1);
  const comparable2 = getComparableDate(date2);

  // If either date is empty, N/A, or TBD, consider them not equal (toggle should be disabled)
  if (
    !comparable1 ||
    !comparable2 ||
    comparable1 === 'N/A' ||
    comparable2 === 'N/A' ||
    comparable1 === 'TBD' ||
    comparable2 === 'TBD'
  ) {
    return true; // Return true to disable toggle when dates are invalid
  }

  return comparable1 === comparable2;
};

/**
 * Component for rendering a group of fields wrapped in a bordered container
 */
export const CustomComponentField: React.FC<CustomComponentFieldProps> = ({
  field,
  value,
  onChange,
  formValues,
  hasSubmitted
}) => {
  // Initialize visibility states for dependent fields
  useEffect(() => {
    field.fieldGroup?.forEach(groupField => {
      if (groupField.type === 'switch' && groupField.dependents) {
        // Get the current switch value
        const switchValue = value?.[groupField.name] || false;

        // For each dependent field of the switch
        groupField.dependents.forEach(dependentField => {
          // If switch is off, hide the dependent field
          if (!switchValue) {
            updateFieldVisibility(dependentField, false);
          }
        });
      }
    });
  }, [field.fieldGroup, value]); // Include field.fieldGroup and value in dependencies

  // Check if showOnRoadmap should be disabled.
  // Compute on every render (no memo) to avoid missing updates when date objects are mutated/reused.
  const marketingDate = formValues?.marketingDate;
  const effectiveDate =
    formValues?.[field.name]?.effectiveDate ?? value?.effectiveDate;

  const isShowOnRoadmapDisabled =
    field.name === 'releaseDateInfo'
      ? !marketingDate ||
        !effectiveDate ||
        areDatesEqual(marketingDate, effectiveDate)
      : false;

  // Auto-set showOnRoadmap to false when dates are the same
  useEffect(() => {
    if (field.name === 'releaseDateInfo' && isShowOnRoadmapDisabled) {
      // Use formValues to get the latest state instead of the potentially stale value prop
      const currentValue = formValues?.[field.name] || value || {};
      const currentShowOnRoadmap = currentValue?.showOnRoadmap;
      if (currentShowOnRoadmap !== false) {
        // Only update if it's not already false to avoid infinite loops
        onChange(field.name, { ...currentValue, showOnRoadmap: false });
      }
    }
  }, [
    field.name,
    isShowOnRoadmapDisabled,
    formValues?.[field.name],
    value,
    onChange
  ]);

  // Create a handler for field changes within the group
  const handleFieldChange = (fieldName: string, newValue: any) => {
    // Use formValues to get the latest state instead of the potentially stale value prop
    // This ensures we always merge with the most up-to-date data
    const currentValue = formValues?.[field.name] || value || {};
    onChange(field.name, { ...currentValue, [fieldName]: newValue });
  };

  return (
    <View UNSAFE_className={styles.customComponentContainer}>
      {field.fieldGroup?.map((groupField, index) => {
        // Pass disabled prop for showOnRoadmap switch
        const isDisabled =
          groupField.name === 'showOnRoadmap' &&
          field.name === 'releaseDateInfo'
            ? isShowOnRoadmapDisabled
            : false;

        return (
          <FormFieldComponent
            key={`${field.name}-${groupField.name}-${index}`}
            field={groupField}
            value={value?.[groupField.name]}
            onChange={val => handleFieldChange(groupField.name, val)}
            formValues={formValues}
            hasSubmitted={hasSubmitted}
            disabled={isDisabled}
          />
        );
      })}
    </View>
  );
};
